import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, Search, FileText, Upload, Loader2, Trash2, Edit, 
  Send, Check, Package, Truck, X, Calendar, DollarSign,
  Mic, Image, FileSpreadsheet, MessageSquare, History,
  ChevronDown, ChevronUp, Building, RefreshCw
} from "lucide-react";
import { BACKEND_URL as __BACKEND_URL__ } from "../../lib/backend-url";

const API = `${__BACKEND_URL__}/api`;

// Status labels and colors
const STATUS_CONFIG = {
  draft: { label: "Rascunho", color: "bg-slate-100 text-slate-700" },
  sent: { label: "Enviado", color: "bg-blue-100 text-blue-700" },
  confirmed: { label: "Confirmado", color: "bg-emerald-100 text-emerald-700" },
  partial: { label: "Parcial", color: "bg-amber-100 text-amber-700" },
  received: { label: "Recebido", color: "bg-green-100 text-green-700" },
  cancelled: { label: "Cancelado", color: "bg-red-100 text-red-700" }
};

export function PurchaseOrdersTab() {
  // State
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Create form state
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [expectedDelivery, setExpectedDelivery] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [notes, setNotes] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);
  
  // AI Analysis state
  const [aiFile, setAiFile] = useState(null);
  const [aiFileType, setAiFileType] = useState("image");
  const [aiTextContent, setAiTextContent] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Load orders
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/purchase-orders`);
      if (response.data.success) {
        setOrders(response.data.orders);
      }
    } catch (error) {
      console.error("Error loading orders:", error);
      toast.error("Erro ao carregar pedidos");
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Load suppliers
  const searchSuppliers = useCallback(async (search = "") => {
    try {
      const response = await axios.get(`${API}/purchase-orders/suppliers/combined`, {
        params: { search, limit: 20 }
      });
      if (response.data.success) {
        setSuppliers(response.data.suppliers);
      }
    } catch (error) {
      console.error("Error searching suppliers:", error);
    }
  }, []);
  
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (supplierSearch.length >= 2) {
        searchSuppliers(supplierSearch);
      } else if (supplierSearch.length === 0) {
        searchSuppliers();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [supplierSearch, searchSuppliers]);
  
  // Add item to order
  const addItem = () => {
    setOrderItems([...orderItems, {
      id: Date.now().toString(),
      product_name: "",
      ean: "",
      quantity: 1,
      unit: "UN",
      unit_price: 0,
      last_purchases: []
    }]);
  };
  
  // Update item
  const updateItem = (id, field, value) => {
    setOrderItems(orderItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };
  
  // Remove item
  const removeItem = (id) => {
    setOrderItems(orderItems.filter(item => item.id !== id));
  };
  
  // Calculate total
  const calculateTotal = () => {
    return orderItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };
  
  // Create order
  const createOrder = async () => {
    if (!selectedSupplier) {
      toast.error("Selecione um fornecedor");
      return;
    }
    if (orderItems.length === 0) {
      toast.error("Adicione pelo menos um item");
      return;
    }
    
    setSavingOrder(true);
    try {
      const response = await axios.post(`${API}/purchase-orders`, {
        supplier_id: selectedSupplier.id,
        supplier_code: selectedSupplier.code,
        supplier_name: selectedSupplier.trade_name || selectedSupplier.name || selectedSupplier.legal_name,
        supplier_cnpj: selectedSupplier.cnpj,
        items: orderItems.map(item => ({
          ean: item.ean,
          product_name: item.product_name,
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        expected_delivery_date: expectedDelivery,
        payment_terms: paymentTerms,
        notes: notes
      });
      
      if (response.data.success) {
        toast.success(`Pedido ${response.data.order.order_number} criado!`);
        setShowCreateModal(false);
        resetForm();
        loadOrders();
      }
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Erro ao criar pedido");
    } finally {
      setSavingOrder(false);
    }
  };
  
  // Reset form
  const resetForm = () => {
    setSelectedSupplier(null);
    setSupplierSearch("");
    setOrderItems([]);
    setExpectedDelivery("");
    setPaymentTerms("");
    setNotes("");
    setAiResult(null);
  };
  
  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/purchase-orders/${orderId}`, { status: newStatus });
      toast.success("Status atualizado");
      loadOrders();
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };
  
  // Delete order
  const deleteOrder = async (orderId) => {
    if (!window.confirm("Tem certeza que deseja excluir este pedido?")) return;
    
    try {
      await axios.delete(`${API}/purchase-orders/${orderId}`);
      toast.success("Pedido excluído");
      loadOrders();
    } catch (error) {
      toast.error("Erro ao excluir pedido");
    }
  };
  
  // AI Analysis
  const analyzeWithAI = async () => {
    if (!aiFile && !aiTextContent) {
      toast.error("Selecione um arquivo ou digite o texto");
      return;
    }
    
    setAnalyzing(true);
    try {
      const formData = new FormData();
      if (aiFile) {
        formData.append("file", aiFile);
      }
      formData.append("file_type", aiFileType);
      if (aiTextContent) {
        formData.append("text_content", aiTextContent);
      }
      if (selectedSupplier) {
        formData.append("supplier_hint", selectedSupplier.trade_name || selectedSupplier.name);
      }
      
      const response = await axios.post(`${API}/purchase-orders/ai/analyze-document`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.success) {
        setAiResult(response.data.extracted_data);
        toast.success("Documento analisado com sucesso!");
        
        // Pre-fill form with extracted data
        if (response.data.extracted_data.supplier) {
          const supplier = response.data.extracted_data.supplier;
          if (supplier.name && !selectedSupplier) {
            setSupplierSearch(supplier.name);
          }
        }
        
        if (response.data.extracted_data.items) {
          const extractedItems = response.data.extracted_data.items.map((item, idx) => ({
            id: `ai-${idx}-${Date.now()}`,
            product_name: item.product_name || "",
            ean: item.ean || "",
            quantity: item.quantity || 1,
            unit: item.unit || "UN",
            unit_price: item.unit_price || 0,
            last_purchases: []
          }));
          setOrderItems(extractedItems);
        }
        
        if (response.data.extracted_data.expected_delivery_date) {
          setExpectedDelivery(response.data.extracted_data.expected_delivery_date);
        }
        if (response.data.extracted_data.payment_terms) {
          setPaymentTerms(response.data.extracted_data.payment_terms);
        }
        if (response.data.extracted_data.notes) {
          setNotes(response.data.extracted_data.notes);
        }
        
        setShowAIModal(false);
      }
    } catch (error) {
      console.error("AI analysis error:", error);
      toast.error("Erro na análise do documento");
    } finally {
      setAnalyzing(false);
    }
  };
  
  // File type icons
  const fileTypeIcons = {
    audio: <Mic className="w-4 h-4" />,
    image: <Image className="w-4 h-4" />,
    pdf: <FileText className="w-4 h-4" />,
    excel: <FileSpreadsheet className="w-4 h-4" />,
    csv: <FileSpreadsheet className="w-4 h-4" />,
    text: <MessageSquare className="w-4 h-4" />
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Pedidos de Compra</h2>
          <p className="text-slate-500 text-sm">Gerencie seus pedidos de compra com fornecedores</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={loadOrders}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-[#4A90E2] hover:bg-[#3A80D2]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Pedido
          </Button>
        </div>
      </div>
      
      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#4A90E2]" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Nenhum pedido encontrado</p>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="mt-4 bg-[#4A90E2] hover:bg-[#3A80D2]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Pedido
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº Pedido</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map(order => (
                  <TableRow key={order.id}>
                    <TableCell className="font-mono font-medium">{order.order_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{order.supplier_name}</p>
                        {order.supplier_cnpj && (
                          <p className="text-xs text-slate-500">{order.supplier_cnpj}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_CONFIG[order.status]?.color || "bg-slate-100"}>
                        {STATUS_CONFIG[order.status]?.label || order.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      R$ {order.total?.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(order.created_at).toLocaleDateString("pt-BR")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {order.status === "draft" && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => updateOrderStatus(order.id, "sent")}
                            title="Marcar como enviado"
                          >
                            <Send className="w-4 h-4 text-blue-500" />
                          </Button>
                        )}
                        {order.status === "sent" && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => updateOrderStatus(order.id, "confirmed")}
                            title="Marcar como confirmado"
                          >
                            <Check className="w-4 h-4 text-emerald-500" />
                          </Button>
                        )}
                        {order.status === "confirmed" && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => updateOrderStatus(order.id, "received")}
                            title="Marcar como recebido"
                          >
                            <Truck className="w-4 h-4 text-green-500" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setSelectedOrder(order)}
                          title="Ver detalhes"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        {order.status === "draft" && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => deleteOrder(order.id)}
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Create Order Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Pedido de Compra</DialogTitle>
            <DialogDescription>Crie um pedido manualmente ou use IA para extrair de um documento</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* AI Import Button */}
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowAIModal(true)}
                className="border-purple-200 text-purple-700 hover:bg-purple-50"
              >
                <Mic className="w-4 h-4 mr-2" />
                Importar com IA
              </Button>
            </div>
            
            {/* Supplier Selection */}
            <div className="space-y-2">
              <Label>Fornecedor *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar fornecedor por nome ou CNPJ..."
                  value={supplierSearch}
                  onChange={(e) => setSupplierSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Supplier Results */}
              {suppliers.length > 0 && !selectedSupplier && supplierSearch && (
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {suppliers.map((s, idx) => (
                    <div 
                      key={idx}
                      onClick={() => {
                        setSelectedSupplier(s);
                        setSupplierSearch(s.trade_name || s.name || s.legal_name);
                      }}
                      className="p-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                    >
                      <p className="font-medium text-sm">{s.trade_name || s.name || s.legal_name}</p>
                      <p className="text-xs text-slate-500">
                        CNPJ: {s.cnpj} • {s.source === "nfe_sync" ? "NF-e" : "PostgreSQL"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Selected Supplier */}
              {selectedSupplier && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-emerald-800">
                      {selectedSupplier.trade_name || selectedSupplier.name || selectedSupplier.legal_name}
                    </p>
                    <p className="text-xs text-emerald-600">CNPJ: {selectedSupplier.cnpj}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setSelectedSupplier(null);
                      setSupplierSearch("");
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            
            {/* Order Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Itens do Pedido</Label>
                <Button variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Item
                </Button>
              </div>
              
              {orderItems.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed rounded-lg text-slate-400">
                  <Package className="w-8 h-8 mx-auto mb-2" />
                  <p>Nenhum item adicionado</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {orderItems.map((item, idx) => (
                    <div key={item.id} className="p-3 border rounded-lg bg-slate-50">
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-4">
                          <Label className="text-xs">Produto</Label>
                          <Input
                            placeholder="Nome do produto"
                            value={item.product_name}
                            onChange={(e) => updateItem(item.id, "product_name", e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">EAN</Label>
                          <Input
                            placeholder="EAN"
                            value={item.ean}
                            onChange={(e) => updateItem(item.id, "ean", e.target.value)}
                            className="h-8 text-sm font-mono"
                          />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs">Qtd</Label>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(item.id, "quantity", e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs">Un</Label>
                          <Input
                            value={item.unit}
                            onChange={(e) => updateItem(item.id, "unit", e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Preço Unit.</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateItem(item.id, "unit_price", e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs">Total</Label>
                          <p className="h-8 flex items-center text-sm font-medium">
                            R$ {(item.quantity * item.unit_price).toFixed(2)}
                          </p>
                        </div>
                        <div className="col-span-1 flex items-end">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeItem(item.id)}
                            className="h-8"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Last Purchases */}
                      {item.last_purchases && item.last_purchases.length > 0 && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                          <p className="font-medium text-blue-700 mb-1">
                            <History className="w-3 h-3 inline mr-1" />
                            Últimas compras:
                          </p>
                          {item.last_purchases.map((lp, i) => (
                            <p key={i} className="text-blue-600">
                              {lp.date}: {lp.quantity} un x R$ {lp.unit_price?.toFixed(2)} = R$ {lp.total?.toFixed(2)}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Total */}
              {orderItems.length > 0 && (
                <div className="flex justify-end">
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Total do Pedido</p>
                    <p className="text-2xl font-bold text-[#4A90E2]">R$ {calculateTotal().toFixed(2)}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Additional Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Prevista de Entrega</Label>
                <Input
                  type="date"
                  value={expectedDelivery}
                  onChange={(e) => setExpectedDelivery(e.target.value)}
                />
              </div>
              <div>
                <Label>Condição de Pagamento</Label>
                <Input
                  placeholder="Ex: 30 dias"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                />
              </div>
            </div>
            
            <div>
              <Label>Observações</Label>
              <Textarea
                placeholder="Observações do pedido..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="h-20"
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                Cancelar
              </Button>
              <Button 
                onClick={createOrder}
                disabled={savingOrder}
                className="bg-[#4A90E2] hover:bg-[#3A80D2]"
              >
                {savingOrder ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando...</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" />Criar Pedido</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* AI Import Modal */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar com IA</DialogTitle>
            <DialogDescription>
              Envie um documento (áudio, PDF, imagem, Excel, texto) e a IA extrairá as informações do pedido
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* File Type Selection */}
            <div>
              <Label>Tipo de Arquivo</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(fileTypeIcons).map(([type, icon]) => (
                  <Button
                    key={type}
                    variant={aiFileType === type ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAiFileType(type)}
                    className={aiFileType === type ? "bg-purple-600 hover:bg-purple-700" : ""}
                  >
                    {icon}
                    <span className="ml-1 capitalize">{type}</span>
                  </Button>
                ))}
              </div>
            </div>
            
            {/* File Upload */}
            {aiFileType !== "text" && (
              <div>
                <Label>Arquivo</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => setAiFile(e.target.files?.[0] || null)}
                  accept={
                    aiFileType === "audio" ? "audio/*" :
                    aiFileType === "image" ? "image/*" :
                    aiFileType === "pdf" ? ".pdf" :
                    aiFileType === "excel" ? ".xlsx,.xls" :
                    aiFileType === "csv" ? ".csv" : "*"
                  }
                  className="hidden"
                />
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-purple-400 transition-colors"
                >
                  {aiFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="w-6 h-6 text-purple-600" />
                      <span className="text-sm font-medium">{aiFile.name}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setAiFile(null); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">Clique para selecionar ou arraste o arquivo</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Text Input */}
            {aiFileType === "text" && (
              <div>
                <Label>Texto do Pedido</Label>
                <Textarea
                  placeholder="Cole aqui o texto do pedido..."
                  value={aiTextContent}
                  onChange={(e) => setAiTextContent(e.target.value)}
                  className="h-40"
                />
              </div>
            )}
            
            {/* AI Result Preview */}
            {aiResult && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm font-medium text-purple-800 mb-2">Dados Extraídos:</p>
                <pre className="text-xs text-purple-600 overflow-auto max-h-32">
                  {JSON.stringify(aiResult, null, 2)}
                </pre>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAIModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={analyzeWithAI}
                disabled={analyzing || (!aiFile && !aiTextContent)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {analyzing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analisando...</>
                ) : (
                  <><Mic className="w-4 h-4 mr-2" />Analisar com IA</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Order Details Modal */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Pedido {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Supplier Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Building className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">Fornecedor</span>
                </div>
                <p className="text-lg font-semibold">{selectedOrder.supplier_name}</p>
                {selectedOrder.supplier_cnpj && (
                  <p className="text-sm text-slate-500">CNPJ: {selectedOrder.supplier_cnpj}</p>
                )}
              </div>
              
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Status:</span>
                <Badge className={STATUS_CONFIG[selectedOrder.status]?.color}>
                  {STATUS_CONFIG[selectedOrder.status]?.label}
                </Badge>
              </div>
              
              {/* Items */}
              <div>
                <Label>Itens ({selectedOrder.items?.length || 0})</Label>
                <div className="mt-2 border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="p-2 flex justify-between items-center">
                      <div>
                        <p className="font-medium text-sm">{item.product_name}</p>
                        {item.ean && <p className="text-xs text-slate-500 font-mono">EAN: {item.ean}</p>}
                      </div>
                      <div className="text-right text-sm">
                        <p>{item.quantity} {item.unit} x R$ {item.unit_price?.toFixed(2)}</p>
                        <p className="font-medium">R$ {item.total_price?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Total */}
              <div className="flex justify-between items-center p-3 bg-[#4A90E2]/10 rounded-lg">
                <span className="font-medium">Total do Pedido</span>
                <span className="text-2xl font-bold text-[#4A90E2]">R$ {selectedOrder.total?.toFixed(2)}</span>
              </div>
              
              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {selectedOrder.expected_delivery_date && (
                  <div>
                    <span className="text-slate-500">Entrega Prevista:</span>
                    <p className="font-medium">{selectedOrder.expected_delivery_date}</p>
                  </div>
                )}
                {selectedOrder.payment_terms && (
                  <div>
                    <span className="text-slate-500">Pagamento:</span>
                    <p className="font-medium">{selectedOrder.payment_terms}</p>
                  </div>
                )}
              </div>
              
              {selectedOrder.notes && (
                <div>
                  <span className="text-sm text-slate-500">Observações:</span>
                  <p className="text-sm">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
